import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, FileSpreadsheet, Receipt, Upload } from 'lucide-react';
import { adminQueries, getErrorMessage, keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { DOCUMENT_CHECK_RECEIPT_FOLDER, formatDate, formatNumber, hasPermission } from '@blimo/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DocumentList,
  ErrorState,
  FileDropzone,
  SearchInput,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from '@blimo/ui';
import { useInvalidateCheck } from './utils.js';

/** WinCasa-Buchungsliste (CSV) und Belege (PDF) – Import; die Zuordnung der Belege macht die API. */
export function BookingListTab({ check, me }) {
  const canManage = hasPermission(me, 'document-checks.manage');
  const receiptFolder = check.folders?.find((f) => f.key === DOCUMENT_CHECK_RECEIPT_FOLDER);
  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle as="h2" className="flex items-center gap-2">
              WinCasa-Buchungsliste
              {check.has_booking_list ? <Badge variant="success">Importiert</Badge> : <Badge variant="neutral">Noch nicht importiert</Badge>}
            </CardTitle>
            <CardDescription>
              Die Kassenprüfer sehen die Buchungsliste im Portal als Tabelle mit Verknüpfung zu den Belegen. Ein erneuter Import ersetzt die bisherige Liste.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {canManage ? (
              <UploadBox
                label="Buchungsliste (CSV)"
                accept=".csv,text/csv"
                multiple={false}
                buttonLabel="Buchungsliste importieren"
                run={(api, files) => api.admin.documentChecks.uploadBookingList(check.id, files[0])}
                success={(r) => `${formatNumber(r?.rows ?? 0)} Zeilen importiert, ${formatNumber(r?.linked_receipts ?? 0)} Belege verknüpft`}
              />
            ) : (
              <p className="text-sm text-muted-foreground">Import nur mit dem Recht „Belegprüfungen verwalten“.</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle as="h2">Belege (PDF)</CardTitle>
            <CardDescription>
              Belege können vor oder nach der Buchungsliste hochgeladen werden; die Zuordnung zu den Zeilen erfolgt über den Dateinamen.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {receiptFolder ? <ReceiptList folder={receiptFolder} me={me} /> : <p className="text-sm text-muted-foreground">Noch keine Belege hochgeladen.</p>}
            {canManage && (
              <UploadBox
                label="Belege"
                accept="application/pdf,.pdf"
                multiple
                maxFiles={200}
                buttonLabel="Belege hochladen"
                run={(api, files) => api.admin.documentChecks.uploadReceipts(check.id, files)}
                success={(r) => `${formatNumber(r?.uploaded ?? 0)} Belege hochgeladen`}
              />
            )}
          </CardContent>
        </Card>
      </div>
      {check.has_booking_list && <BookingRows checkId={check.id} canOpenReceipts={hasPermission(me, 'documents.view')} />}
    </div>
  );
}

/** Rows of the WinCasa booking list: cells may be a list or an object (raw CSV columns). */
const cellsOf = (row) => (Array.isArray(row.cells) ? row.cells : Object.values(row.cells ?? {}));

/** Imported rows (GET …/booking-list, server-side search over the cells, paginated). 404 = nothing imported → hidden. */
function BookingRows({ checkId, canOpenReceipts }) {
  const api = useApi();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const query = useQuery(adminQueries.documentCheckBookingList(api, checkId, { search: search || undefined, page }));
  if (query.error?.status === 404) return null;
  const body = query.data?.data;
  const meta = query.data?.meta;
  const header = Array.isArray(body?.header) ? body.header : body?.header ? Object.values(body.header) : [];
  const rows = body?.rows ?? [];
  const lastPage = Math.max(1, meta?.last_page ?? 1);

  return (
    <Card>
      <CardHeader>
        <CardTitle as="h2">Gebuchte Zeilen</CardTitle>
        <CardDescription>
          {[body?.bank, body?.period_from && body?.period_to ? `${formatDate(body.period_from)} – ${formatDate(body.period_to)}` : null]
            .filter(Boolean)
            .join(' · ') || 'Inhalt der importierten Buchungsliste.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <SearchInput
          label="Buchungsliste durchsuchen"
          placeholder="Suchen (alle Zeilen) …"
          value={search}
          onValueChange={(v) => {
            setSearch(v);
            setPage(1);
          }}
        />
        {query.error ? (
          <ErrorState compact error={query.error} onRetry={() => query.refetch()} />
        ) : query.isPending ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <div className={query.isPlaceholderData ? 'opacity-60 transition-opacity' : undefined}>
            <Table aria-label="Buchungsliste" containerClassName="max-h-[32rem] rounded-md border">
              {header.length > 0 && (
                <TableHeader>
                  <TableRow>
                    <TableHead data-numeric="true">Zeile</TableHead>
                    {header.map((h, i) => (
                      <TableHead key={i}>{h}</TableHead>
                    ))}
                    <TableHead>Beleg</TableHead>
                  </TableRow>
                </TableHeader>
              )}
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={header.length + 2} className="text-center text-muted-foreground">
                      Keine Buchungen gefunden.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow key={row.row}>
                      <TableCell data-numeric="true">{row.row}</TableCell>
                      {cellsOf(row).map((cell, i) => (
                        <TableCell key={i}>{cell}</TableCell>
                      ))}
                      <TableCell>
                        {row.receipt_document_id && canOpenReceipts ? (
                          <a
                            href={api.admin.documents.downloadUrl(row.receipt_document_id, { inline: true })}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-primary hover:underline"
                            aria-label={`Beleg zu Zeile ${row.row} öffnen`}
                          >
                            <Receipt className="size-3.5" aria-hidden="true" /> Beleg
                          </a>
                        ) : row.receipt_document_id ? (
                          <Badge variant="neutral">verknüpft</Badge>
                        ) : (
                          <span className="text-muted-foreground">–</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
        <div className="flex items-center justify-between gap-2 text-sm">
          <span className="text-muted-foreground">
            Seite {page} von {lastPage} · {formatNumber(meta?.total ?? 0)} Buchungen
          </span>
          {lastPage > 1 && (
            <div className="flex gap-1">
              <Button variant="outline" size="icon" disabled={page <= 1} aria-label="Vorherige Seite" onClick={() => setPage(page - 1)}>
                <ChevronLeft aria-hidden="true" />
              </Button>
              <Button variant="outline" size="icon" disabled={page >= lastPage} aria-label="Nächste Seite" onClick={() => setPage(page + 1)}>
                <ChevronRight aria-hidden="true" />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ReceiptList({ folder, me }) {
  const api = useApi();
  const query = useQuery({ ...adminQueries.folderDocuments(api, folder.id), enabled: hasPermission(me, 'documents.view') });
  if (!hasPermission(me, 'documents.view')) return <p className="text-sm text-muted-foreground">{formatNumber(folder.documents_count ?? 0)} Belege</p>;
  if (query.isPending) return <Skeleton className="h-16 w-full" />;
  return (
    <div className="max-h-72 overflow-y-auto">
      <DocumentList label="Belege" documents={query.data} hrefFor={(doc) => api.admin.documents.downloadUrl(doc.id)} emptyText="Noch keine Belege." />
    </div>
  );
}

/** @param {{ label: string, accept: string, multiple: boolean, maxFiles?: number, buttonLabel: string, run: (api: any, files: File[]) => Promise<any>, success: (result: any) => string }} props */
function UploadBox({ label, accept, multiple, maxFiles, buttonLabel, run, success }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const invalidate = useInvalidateCheck();
  const [files, setFiles] = useState(/** @type {File[]} */ ([]));
  const [pending, setPending] = useState(false);

  async function submit() {
    setPending(true);
    try {
      const result = await run(api, files);
      toast.success(success(result));
      setFiles([]);
      queryClient.invalidateQueries({ queryKey: keys.admin.resource('document-folders') });
      invalidate();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Der Upload ist fehlgeschlagen.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <FileDropzone label={label} files={files} onFilesChange={setFiles} accept={accept} multiple={multiple} maxFiles={maxFiles} disabled={pending} />
      {files.length > 0 && (
        <Button size="sm" className="w-fit" onClick={submit} loading={pending}>
          {multiple ? <Upload aria-hidden="true" /> : <FileSpreadsheet aria-hidden="true" />} {buttonLabel}
        </Button>
      )}
    </div>
  );
}
